import { useEffect, useState } from 'react';
import { Card, Form, Spinner, Alert, Placeholder, Row, Col } from 'react-bootstrap';
import { useLocation } from "react-router-dom";
import { getQueryParams } from "helpers/common";
import { setAxiosAccessToken } from "helpers/Http";
import { getEmailPreferenceList, saveEmailPreference } from "store/services/emailPreferenceService";
import { SUCCESS_MESSAGE } from "common/constantMessage";

const EmailPreferences = () => {
  const [preferences, setPreferences] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [loadingToggles, setLoadingToggles] = useState({});
  const [userEmail, setUserEmail] = useState(null);

  const location = useLocation();
  const queryParams = getQueryParams(location.search);

  useEffect(() => {
    if (queryParams?.token) {
      getPreferencesList(queryParams?.token);
    }
  }, [queryParams?.token]);

  const getPreferencesList = async (token) => {
    try {
      setLoading(true);
      const response = await getEmailPreferenceList(token);
      setPreferences(response?.body?.subscriptions || []);
      setUserEmail(response?.body?.user_email);
    } catch (err) {
      console.error("Error getting preferences list", err);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePreference = async (moduleIndex, prefIndex) => {
    const updated = [...preferences];
    const pref = updated[moduleIndex].preferences[prefIndex];
    const newStatus = pref.subscribed === 'yes' ? 'no' : 'yes';

    setLoadingToggles(prev => ({ ...prev, [pref.email_type_id]: true }));

    pref.subscribed = newStatus;
    setPreferences(updated);

    try {
      await saveEmailPreference(queryParams?.token, pref.email_type_id, { subscribe: newStatus });
      SUCCESS_MESSAGE("Preferences saved successfully", { toastId: "preference-save-success" });
    } catch (error) {
      console.error('Failed to update preference:', error);
      pref.subscribed = newStatus === 'yes' ? 'no' : 'yes';
      setPreferences([...updated]);
    }

    setLoadingToggles(prev => ({ ...prev, [pref.email_type_id]: false }));
  };

  return (
    <div className="container mt-5 mb-5">
      <Row>
        <Col md="8" className="mx-auto">
          <h3 className="mb-2 fw-bold">Manage Email Notifications</h3>
          <p className="mb-2 text-muted">Choose which notifications you'd like to receive.</p>
          {userEmail && (
            <p className="mb-4">Preferences linked to <strong>{userEmail}</strong></p>
          )}
          {isLoading ? (
            <>
              {[1, 2, 3].map((item) => (
                <Card className="mb-3 shadow-sm rounded-4 border-0" key={item}>
                  <Card.Body>
                    <Placeholder as={Card.Title} animation="wave">
                      <Placeholder xs={6} />
                    </Placeholder>
                    <Placeholder as={Card.Text} animation="wave">
                      <Placeholder xs={8} />
                    </Placeholder>
                  </Card.Body>
                </Card>
              ))}
            </>
          ) : preferences.length > 0 ? (
            preferences.map((module, moduleIndex) => (
              <Card className="mb-4 shadow-sm rounded-4 border-1" key={module.module_id}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Card.Title className="fw-semibold">{module.module_name}</Card.Title>
                  </div>
                  <Card.Text className="text-muted mb-4">{module.description}</Card.Text>

                  {module.preferences.map((pref, prefIndex) => (
                    <div
                      key={pref.email_type_id}
                      className={`d-flex align-items-center justify-content-between py-2 ${
                        prefIndex !== module.preferences.length - 1 ? 'border-bottom' : ''
                      }`}
                    >
                      <div>
                        <div>
                          <p className="m-0 fw-medium">{pref.name}</p>
                        </div>
                        {pref.description && (
                          <div className="text-muted">{pref.description}</div>
                        )}
                      </div>

                      <div className="d-flex align-items-center">
                        {loadingToggles[pref.email_type_id] ? (
                          <Spinner animation="border" size="sm" className="me-2" />
                        ) : (
                          <Form.Check
                            type="switch"
                            id={`switch-${module.module_id}-${pref.email_type_id}`}
                            checked={pref.subscribed === 'yes'}
                            onChange={() => handleTogglePreference(moduleIndex, prefIndex)}
                            disabled={loadingToggles[pref.email_type_id]}
                            className="ms-3"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            ))
          ) : (
            <Alert variant="warning">No preferences found.</Alert>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default EmailPreferences;
